import { tryParseJSON } from '@/utils/tryParseJSON';
import { fixedLengthNumber } from '@/utils/fixedLengthNumber';
import { fireBuyItNowTaglessTracker } from '@/ads/bin-tracker';
import buildIdGraph from './build-id-graph';

const buyItNowBids = [];
const _floorsByBidId = window._floorsByBidId ||= new Map();

/* helper for extracting numeric bidFloor */
const extractFloor = p => {
	if (Array.isArray(p)) {
		return p[0]?.bidFloor ?? p[0]?.floor ?? null;
	}
	return p?.bidFloor ?? p?.floor ?? null;
};

window._binAliases = window._binAliases || new Set();
const BIN_GVLIDS = { rubicon: 52, triplelift: 28, msft: 32 };

const ensureBinAlias = (baseBidder) => {
	const alias = `${baseBidder}-bin`;
	if (window._binAliases.has(alias)) return alias;
	try {
		const opts = BIN_GVLIDS[baseBidder] ? { gvlid: BIN_GVLIDS[baseBidder] } : undefined;
		pbjs.aliasBidder(baseBidder, alias, opts);
		window._binAliases.add(alias);
	} catch (e) {
		window.awxLogger?.withScope('Ads::ensureBinAlias')
			.debug(`Failed to alias bin bidder ${baseBidder} -> ${alias}`, e);
	}
	return alias;
};

export const getPrebidConfig = () => {
	const { ppid, ortbConfig, userData, platform, hashedEmail } = window.globalAdConfig;

	let ortb2Geo = {};
	if (window.adInfo.country?.toLowerCase() === 'us') {
		ortb2Geo = {
			country: 'usa',
			region: window.adInfo.state,
			metro: window.adInfo.dma,
			city: window.adInfo.city,
			zip: window.adInfo.zip,
			type: 3,
			lat: fixedLengthNumber(window.adInfo.lat, 4),
			lon: fixedLengthNumber(window.adInfo.lon, 4),
		};
	}

	const ortb2User = { data: {} };
	['userid', 'userid3p', 'partner', 'pgview', 'pt', 'viewport', 'hour'].forEach(
		(key) => {
			if (window.adInfo && window.adInfo[key]) {
				ortb2User.data[key] = window.adInfo[key].replaceAll('-', 'neg');
			}
		},
	);

	const currentUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
	const ortb2Site = {
		name: ortbConfig.name || 'AccuWeather',
		domain: ortbConfig.domain || 'www.accuweather.com',
		cat: ortbConfig.categories || [
			'IAB-7',
			'IAB7-4',
			'IAB15-10',
			'IAB20',
			'288',
			'390',
			'653',
		],
		page: currentUrl,
		ref: document.referrer,
		mobile: ortbConfig.isOptimizedForMobile ? 1 : 0,
		privacypolicy: ortbConfig.hasPrivacyPolicy ? 1 : 0,
		publisher: {
			name: ortbConfig.publisher.name || 'AccuWeather',
			domain: ortbConfig.publisher.domain || 'www.accuweather.com',
			cat: ortbConfig.publisher.categories || [
				'IAB-7',
				'IAB7-4',
				'IAB15-10',
				'IAB20',
				'288',
				'390',
				'653',
			],
		},
		content: {
			language: window.adInfo.lang?.substring(0, 2),
			title: document.title,
			keywords: ortbConfig.content.keywords,
			url: currentUrl,
			cat: ortbConfig.content.categories,
			prodq: parseInt(ortbConfig.content.productQuality),
			context: parseInt(ortbConfig.content.context),
			sourcerelationship:
				typeof ortbConfig.content.sourceRelationship === 'number'
					? ortbConfig.content.sourceRelationship
					: (ortbConfig.content.sourceRelationship?.toLowerCase() === 'direct' ? 1 : 0),
			data: [
				{
					name: ortbConfig.content.data.name,
					ext: {
						...ortbConfig.content.data.extensions,
						segtax: parseInt(ortbConfig.content.data.extensions.segtax),
					},
					segment: ortbConfig.content.data.segments,
				},
			],
		},
		sectioncat: ortbConfig.sectionCategories,
		pagecat: ortbConfig.pageCategories,
		keywords: ortbConfig.keywords,
		ext: {
			data: {},
		},
	};

	[
		'advelvet',
		'country',
		'cuhd',
		'cuhi',
		'cuuv',
		'cuwd',
		'cuwx',
		'dma',
		'fdate',
		'state',
		'wx_seg',
		'ufdb',
		'alertscategory',
		'alertssource',
		'alertstypeid',
		'site',
		'realfeel',
	].forEach((key) => {
		if (window.adInfo && window.adInfo[key]) {
			ortb2Site.ext.data[key] = window.adInfo[key]
				.replaceAll('-', 'neg')
				.split(',');
		}
	});

	if (window.adsOnPage.has('top')) {
		const { upr, bidders } = window.adsOnPage.get('top');
		const binTop = bidders.find(
			(bid) => bid.bidder === 'rubicon' && bid.isBuyItNow,
		);
		ortb2Site.ext.data.bin_top = binTop?.params?.floor;
		ortb2Site.ext.data.upr_top = upr;
	}

	const config = {
		debug: window.debugDataLayer?.prebidDebug || window.location.search.includes('pbjs_debug=true'),
		bidderTimeout: window.globalAdConfig?.prebidTimeout,
		enableSendAllBids: false,
		disableAjaxTimeout: true,
		maxRequestsPerOrigin: 6,
		enableTIDs: true,
		bidderSequence: 'fixed',
		priceGranularity: {
			buckets: [
				{
					precision: 2,
					max: 3,
					increment: 0.01,
				},
				{
					precision: 2,
					max: 8,
					increment: 0.05,
				},
				{
					precision: 2,
					max: 20,
					increment: 0.5,
				},
				{
					precision: 2,
					max: 100,
					increment: 1,
				},
			],
		},
		userSync: {
			filterSettings: {
				iframe: {
					bidders: '*',
					filter: 'exclude',
				},
			},
			syncsPerBidder: 3,
			aliasSyncEnabled: true,
			syncDelay: 3000,
			userIds: [
				{
					name: 'sharedId',
					storage: {
						type: 'cookie',
						name: '_pubcid',
						expires: 30,
					},
				},
				{
					name: 'criteo',
				},
				{
					name: '33acrossId',
					params: {
						pid: '0014000000xvF2DAAU',
					},
					storage: {
						name: '33acrossId',
						type: 'html5',
						expires: 90,
						refreshInSeconds: 8 * 3600,
					},
				},
				{
					name: 'unifiedId',
					params: {
						partner: 'vtlti0n',
					},
				},
			],
		},
		deviceAccess: true,
		cache: { allowVastXmlOnly: true, ignoreBidderCacheKey: true, useLocal: true },
		rubicon: {
			singleRequest: true,
		},
		yahoossp: {
			endpoint: 'https://c2shb.ssp.yahoo.com/bidRequest',
			singleRequestMode: true,
		},
		useBidCache: false,
		bidCacheFilterFunction: (bid) => bid.mediaType !== 'video',
		timeoutBuffer: 0,
		ortb2: {
			site: ortb2Site,
			user: {
				ext: ortb2User,
				geo: ortb2Geo,
			},
			device: {
				geo: {
					type: 2,
					lat: fixedLengthNumber(userData.lat, 4),
					lon: fixedLengthNumber(userData.long, 4),
					city: userData.city,
					country: userData.countryCode,
					metro: String(userData.dma),
					region: userData.regionCode,
					zip: userData.zipCodes?.[0]?.primary,
				},
			},
		},
	};

	// Add webview context
	if (window.adInfo.site === 'one') {
		config.ortb2.site.ext.context = { environment: "webview", site_page: currentUrl };
		config.ortb2.device.devicetype = 1;
		config.ortb2.device.ua = window.navigator.userAgent;
	}

	// Add ConnectID to userIds
	const connectIdUserId = {
		name: 'connectId',
		params: {
			pixelId: '58872',
		},
	};

	if (hashedEmail) {
		connectIdUserId.params.he = hashedEmail;
	} else {
		connectIdUserId.params.puid = ppid;
	}

	config.userSync.userIds.push(connectIdUserId);

	// Add UID2 to userIds
	if (hashedEmail) {
		config.userSync.userIds.push({
			name: 'uid2',
			params: {
				serverPublicKey: 'UID2-X-P-MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEPKcMe5poNz1E2xltVpOwbp4txbEYCsrr8xkSEXwKamd2LoSSvBJCL4hCMv2Ad9elOUpMRnAUp7pdbzcNoP+SNQ==',
				subscriptionId: 'xwoSMLaGio',
				emailHash: hashedEmail,
			},
		});
	}

	// Add pubProvidedId to userIds
	const ppUserId = {
		name: 'pubProvidedId',
		params: {
			eids: [
				{
					source: 'accuweather.com',
					uids: [
						{
							id: ppid,
							atype: 1,
							ext: { stype: 'ppuid' },
						},
					],
				},
			],
		},
	};

	config.userSync.userIds.push(ppUserId);

	// Set consent management based on user location
	config.consentManagement = {
		gpp: {
			cmpApi: 'iab',
			timeout: 8000,
		},
	};

	if (isCountryEEA(userData.countryCode)) {
		config.consentManagement.gdpr = {
			cmpApi: 'iab',
		};
	}

	return config;
}

export const prebidSetup = (config) => {
	pbjs.setConfig(config);
	buildIdGraph();
	pbjs.bidderSettings = {
		standard: {
			storageAllowed: true,
		},
	};

	// Set Criteo bidder config
	if (window.globalAdConfig.hashedEmail) {
		pbjs.setBidderConfig({
			bidders: ['criteo'],
			config: {
				ortb2: {
					user: {
						ext: {
							data: {
								eids: [{
									source: 'accuweather.com',
									uids: [
										{
											id: window.globalAdConfig.hashedEmail,
											atype: 3,
											ext: {
												stype: 'hemsha256'
											}
										}
									]
								}]
							}
						}
					}
				}
			}
		});
	}

	// Keep track of winning buy it now bids
	pbjs.onEvent('auctionInit', (auction) => {
		auction.bidderRequests.forEach((bidder) => {
			bidder.bids.forEach((bid) => {
				if (bid.isBuyItNow) {
					buyItNowBids.push(bid.bidId);
				}
			});
		});
	});

	/* remember floors when bids are requested */
	pbjs.onEvent('bidRequested', req => {
		(req.bids || []).forEach(b => {
			let f = parseFloat(extractFloor(b.params));

			if (!Number.isFinite(f)) {
				f = 0;
			}
			const key = b.requestId || b.bidId;
			_floorsByBidId.set(key, f);
		});
	});

	/* veto low-CPM bids on response */
	pbjs.onEvent('bidResponse', bid => {
		const key = bid.requestId || bid.bidId;
		const floor = _floorsByBidId.get(key);
		if (floor == null) return;
		if (bid.cpm < floor) {
			bid.cpm = 0;
			bid.ad = bid.nurl = '';
			bid.bidderResponseDelivered = true;
			bid.status = 'rejected';
		}
	});
};

const ALLOWED_SIZES = {
	top: [
		'300x250',
		'320x50',
		'300x50',
		'336x280',
		'728x90',
		'970x250',
		'970x90',
	],
	top_right: ['300x250', '300x600', '160x600'],
	bottom_right: ['300x250', '300x600', '160x600'],
	bottom: [
		'300x250',
		'320x50',
		'300x50',
		'300x600',
		'336x280',
		'728x90',
		'970x250',
		'970x90',
	],
	adhesion: ['320x50', '300x50'],
	middle: ['300x250', '320x50', '300x50', '300x600', '336x280'],
	native: ['1x1', '2x2', '300x250'],
	infeed: ['1x1', '2x2'],
};

const EXCLUDED_SLOTS = ['oop', 'interstitial', 'rewarded'];

const buildMediaTypes = (sizes, slot) => {
	const allowedSizes = ALLOWED_SIZES[slot] || [];
	const mediaTypes = {
		banner: {
			sizes: sizes.filter(
				(size) => Array.isArray(size) && allowedSizes.indexOf(size.join('x')) > -1
			),
		},
	};

	if (slot === 'native' || slot === 'infeed') {
		mediaTypes.native = { type: 'image' };
	}

	if (sizes.includes('video')) {
		mediaTypes.video = {
			context: 'instream',
			playerSize: [320, 180],
			mimes: ['video/mp4', 'video/webm', 'video/x-ms-wmv', 'application/javascript'],
			protocols: [1, 2, 3, 4, 5, 6, 7, 8],
			playbackmethod: [6],
			pos: 1,
			api: [1, 2, 7],
			linearity: 1,
			placement: 1,
			plcmt: 2,
			startdelay: 0,
			playback_method: ['auto_play_sound_off'],
			minduration: 5,
			maxduration: 35,
			skip: 1,
			skipmin: 30,
			skipafter: 5,
			w: 320,
			h: 180,
			delivery: [3],
			playbackend: 1,
			boxingallowed: 1,
			rewarded: 0,
		};
	}

	return mediaTypes;
};

export const addPrebidAdUnits = () => {
	window.adsOnPage.forEach((adObject) => {
		const { sizes, slot, adUnitCode, tid } = adObject;
		let { bidders } = adObject;

		if (EXCLUDED_SLOTS.includes(slot) || !bidders) {
			return;
		}

		// Exclude google bids
		bidders = bidders.filter((bid) => bid.bidder !== 'google');

		// Separate into BuyItNow and non-BIN bids
		const bids = bidders.filter((bid) => !bid.isBuyItNow);
		const binBids = bidders.filter((bid) => bid.isBuyItNow);

		const mediaTypes = buildMediaTypes(sizes, slot);

		window.logAdEvent(`Prebid: Add ad unit: ${adUnitCode}`);
		if (pbjs.addAdUnits) {
			const pbjsAdUnit = {
				bids,
				mediaTypes,
				code: adUnitCode,
				ortb2Imp: {
					exp: 60,
					ext: {
						gpid: adUnitCode,
						tid: tid,
					}
				},
			};

			if (window.adInfo.site === 'one') {
				pbjsAdUnit.ortb2Imp.ext.webview = 1;
			}

			pbjs.addAdUnits(pbjsAdUnit);
		}

		// Create separate prebid ad unit for BIN bids (ONE-4460) when waiting for prebid (ONE-279)
		if (window.globalAdConfig.disableInitialAdLoad && binBids.length) {
			const binAdUnitCode = `${adUnitCode}-bin`;

			const aliasedBinBids = binBids.map((bid) => {
				const alias = ensureBinAlias(bid.bidder);
				return { ...bid, bidder: alias };
			});

			if (pbjs.addAdUnits) {
				const { video: _video, ...binMediaTypes } = mediaTypes;
				const pbjsBinAdUnit = {
					bids: aliasedBinBids,
					mediaTypes: binMediaTypes,
					code: adUnitCode,
					ortb2Imp: {
						exp: 60,
						ext: { gpid: binAdUnitCode },
					},
				};
				if (window.adInfo.site === 'one') {
					pbjsBinAdUnit.ortb2Imp.ext.webview = 1;
				}
				pbjs.addAdUnits(pbjsBinAdUnit);
			}
		}
	});
};

export const addOpenAdsAdUnits = () => {
	window.adsOnPage.forEach((adObject) => {
		const { sizes, slot, adUnitCode, bidders } = adObject;

		if (EXCLUDED_SLOTS.includes(slot) || !bidders) {
			return;
		}

		const mediaTypes = buildMediaTypes(sizes, slot);

		// Filter bids for The Trade Desk only, non-BIN bids and rename bidder to 'thetradedesk'
		const ttdBids = bidders
			.filter((bid) => bid.bidder === 'ttd' && !bid.isBuyItNow)
			.map((bid) => ({ ...bid, bidder: 'thetradedesk' }));

		if (ttdBids.length && oajs.addAdUnits) {
			window.logAdEvent(`Open Ads: Add ad unit: ${adUnitCode}`);

			// Move bidfloor and currency from params to ortb2Imp
			const ttdBid = ttdBids[0];
			const ortb2Imp = {
				exp: 60,
				ext: { gpid: adUnitCode }
			};

			if (ttdBid?.params?.bidfloor) {
				ortb2Imp.bidfloor = ttdBid.params.bidfloor;
				delete ttdBid.params.bidfloor;
			}

			if (ttdBid?.params?.currency) {
				ortb2Imp.bidfloorcur = ttdBid.params.currency;
				delete ttdBid.params.currency;
			}

			oajs.addAdUnits({
				bids: ttdBids,
				mediaTypes,
				code: adUnitCode,
				ortb2Imp,
			});
		}
	});
};

export const fetchPrebidBids = (adUnitCodes = []) => {
	if (window.globalAdConfig.isPrebidDisabled || document.visibilityState === 'hidden') {
		return Promise.resolve();
	}

	let isBidRequested = false;

	return new Promise((resolve) => {
		pbjs.que.push(() => {
			if (isBidRequested) {
				return resolve();
			}
			isBidRequested = true;
			try {
				window.logAdEvent('Prebid: Request bids start. Ad units:', adUnitCodes);
				pbjs.requestBids({
					adUnitCodes,
					timeout: window.globalAdConfig.prebidTimeout,
					bidsBackHandler: (prebidBids) => {
						window.logAdEvent('Prebid: Request bids complete');
						resolve(prebidBids);
					},
				});
			} catch (error) {
				console.error('Error pbjs.requestBids', error);
				resolve();
			}
		});
	});
};

export const fetchOpenAdsBids = (adUnitCodes = []) => {
	if (window.globalAdConfig.isPrebidDisabled || !window.openAdsEnabled || document.visibilityState === 'hidden') {
		return Promise.resolve();
	}

	let isBidRequested = false;

	return new Promise((resolve) => {
		oajs.que.push(() => {
			if (isBidRequested) {
				return resolve();
			}
			isBidRequested = true;
			try {
				window.logAdEvent('Open Ads: Request bids start');
				oajs.requestBids({
					adUnitCodes,
					timeout: window.globalAdConfig.prebidTimeout,
					bidsBackHandler: (oaBids) => {
						window.logAdEvent('Open Ads: Request bids complete');
						resolve(oaBids);
					},
				});
			} catch (error) {
				console.error('Error oajs.requestBids', error);
				resolve();
			}
		});
	});
};

const renderBidWithIframe = (adDivId, bid) => {
	const adDivEl = document.getElementById(adDivId);
	adDivEl.innerHTML = '';
	const isNotForSafeFrame = bid.width <= 2 || bid.height <= 2;
	const isOpenAdsBid = !!bid.adserverTargeting?.oa_bidder;

	const iframe = document.createElement('iframe');

	if (!isNotForSafeFrame) {
		const safeframeUrl = 'https://www.awxcdn.com/safeframe/1-0-0/html/container.html';
		iframe.setAttribute(
			'src',
			isOpenAdsBid ? `${safeframeUrl}?oajs=true` : safeframeUrl,
		);
		iframe.setAttribute(
			'sandbox',
			'allow-forms allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-top-navigation-by-user-activation',
		);
		iframe.setAttribute('name', bid.adId);
		iframe.setAttribute('height', bid.height);
		iframe.setAttribute('width', bid.width);
	}

	iframe.setAttribute('style', 'display:block;margin:auto');
	iframe.setAttribute('frameborder', 0);
	iframe.setAttribute('scrolling', 'no');
	iframe.setAttribute('marginheight', 0);
	iframe.setAttribute('marginwidth', 0);
	iframe.setAttribute('TOPMARGIN', 0);
	iframe.setAttribute('LEFTMARGIN', 0);
	iframe.setAttribute('allowtransparency', 'true');
	adDivEl.appendChild(iframe);

	if (isNotForSafeFrame) {
		const renderer = isOpenAdsBid ? oajs : pbjs;
		renderer.renderAd(iframe.contentWindow.document, bid.adId);
	}
};
window.renderBidWithIframe = renderBidWithIframe;

export const getHighestBuyItNowBid = (adUnitCode) => {
	let highestBid = { cpm: 0 };

	if (pbjs?.getBidResponsesForAdUnitCode) {
		const bidResponses = pbjs.getBidResponsesForAdUnitCode(adUnitCode);

		// Get the BuyItNow bid with highest cpm
		bidResponses.bids.forEach((bid) => {
			if (
				buyItNowBids.indexOf(bid.requestId) > -1 &&
				bid.cpm > highestBid.cpm
			) {
				highestBid = bid;
			}
		});
	}

	return highestBid;
};

export const maybeRenderBuyItNow = (adObject) => {
	const { adUnitCode, adDivId } = adObject;

	const highestBid = getHighestBuyItNowBid(adUnitCode);
	if (highestBid.cpm > 0) {
		googletag.pubads().setTargeting('pug', '1');

		if (window.globalAdConfig.buyItNowSkipGoogleAdManager) {
			// Mark winner.auction 1 if a buy it now bid wins
			adObject.winner.auction = 1;

			renderBidWithIframe(adDivId, highestBid);

			// Fire tagless tracker iframe
			fireBuyItNowTaglessTracker({
				adUnitCode,
				adObject,
				customTargeting: highestBid.adserverTargeting,
			});

			adObject.gptSlot = null;

			return true;
		}
	}

	return false;
};

const getUprTargeting = (adObject) => {
	let uprTargeting = {
		upr: adObject.upr,
		upr_0: 'no_bid',
		upr_auction: 'skipped',
		hb_bid_1st: 'no_bid',
		hb_bid_2nd: 'no_bid',
	};

	if (pbjs?.getHighestCpmBids) {
		const highestPrebid = pbjs?.getHighestCpmBids
			? pbjs.getHighestCpmBids(adObject.adUnitCode)
			: [];
		const prebidCPM = highestPrebid.length ? highestPrebid[0].cpm || 0 : 0;
		const round = (n) => {
			if (n > 15) {
				return '15.00';
			} else if (n > 10) {
				return String(Math.round(n).toFixed(2));
			} else {
				// Round to closest half
				return String((Math.round(n * 2) / 2).toFixed(2));
			}
		};
		const upr_0 = prebidCPM ? round(prebidCPM) : 'no_bid';
		let hb_bid_1st = 'no_bid';
		let hb_bid_2nd = 'no_bid';

		if (pbjs?.getBidResponsesForAdUnitCode) {
			const formatCpm = (cpm) => (Math.round(cpm * 10) / 10).toFixed(2);
			const bidResponses = pbjs.getBidResponsesForAdUnitCode(
				adObject.adUnitCode,
			).bids;
			bidResponses.sort((a, b) => b.cpm - a.cpm);
			if (bidResponses[0]) {
				hb_bid_1st = formatCpm(bidResponses[0].cpm);
			}
			if (bidResponses[1]) {
				hb_bid_2nd = formatCpm(bidResponses[1].cpm);
			}
		}

		uprTargeting = {
			upr: adObject.upr,
			upr_0,
			upr_auction: 'prebid',
			userid3pbid: `${window.adInfo.userid3p}-${upr_0}`,
			hb_bid_1st,
			hb_bid_2nd,
		};
	}

	return uprTargeting;
};
window.getUprTargeting = getUprTargeting;

/**
 * Checks if a country code is part of the European Economic Area (EEA)
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {boolean} - Returns true if the country is part of the EEA, false otherwise
 */
const isCountryEEA = (countryCode = '') => {
	// Array of EEA country codes (EU member states + Iceland, Liechtenstein, and Norway)
	const eeaCountries = new Set([
		// EU member states
		'AT', // Austria
		'BE', // Belgium
		'BG', // Bulgaria
		'HR', // Croatia
		'CY', // Cyprus
		'CZ', // Czech Republic
		'DK', // Denmark
		'EE', // Estonia
		'FI', // Finland
		'FR', // France
		'DE', // Germany
		'GR', // Greece
		'HU', // Hungary
		'IE', // Ireland
		'IT', // Italy
		'LV', // Latvia
		'LT', // Lithuania
		'LU', // Luxembourg
		'MT', // Malta
		'NL', // Netherlands
		'PL', // Poland
		'PT', // Portugal
		'RO', // Romania
		'SK', // Slovakia
		'SI', // Slovenia
		'ES', // Spain
		'SE', // Sweden

		// EFTA members that are part of the EEA
		'IS', // Iceland
		'LI', // Liechtenstein
		'NO', // Norway
	]);

	// Check if the country code exists in the EEA countries array
	return eeaCountries.has(countryCode.toUpperCase());
};
